import { useEffect, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { experiments } from '@/utils/experiment/manifest';
import { EnrollmentType } from '@/utils/experiment/use-experiment';
import { useExperiment } from '@/utils/experiment/use-experiment';

export type LoomLandOptInCohort = 'control' | 'radio' | 'longText';

/**
 * How long an enrolled visitor may wait for their arm to resolve before we give
 * up and fall back to the Loom-only signup (`statsigError`). WAC bootstraps
 * Statsig values from SSR, so a healthy arm resolves on first render; an arm
 * still unresolved after this is a Statsig failure, not a slow network.
 */
export const COHORT_RESOLVE_TIMEOUT_MS = 5_000;

/**
 * Read-only cohort accessor for the TWCG-2108 Loom Land opt-in experiment.
 *
 * This hook only reads the arm — it does NOT fire an exposure (the manifest sets
 * `fireExperimentExposure: false`). Exposure is fired post-identity on the
 * logged-in site-creation screen (see use-loom-land-exposure.ts); the
 * `choose` screen just reads the cohort here to render the right arm.
 *
 * `cohort` defaults to `'control'` until the arm resolves, so callers MUST gate
 * cohort-dependent side effects (e.g. redirects) on `isResolved`.
 *
 * `isEnrolled` is separate from `isResolved` so callers can tell apart the
 * transient "enrolled but arm not resolved yet" window (wait, render nothing)
 * from "definitively not in the experiment" — e.g. a visitor who lands on the
 * choose page without Loom intent or while signed out — which should fall through
 * to the standard signup flow now. Both cases have `isResolved === false`. See
 * loom-choice-page.template.tsx.
 *
 * If Statsig fails (or has no value for this experiment) the hook keeps
 * reporting enrolled-but-unresolved indefinitely — it is not reported as "not
 * enrolled". `hasResolveTimedOut` flips (and stays) true once that window
 * exceeds {@link COHORT_RESOLVE_TIMEOUT_MS} — or immediately if Statsig has
 * already initialised without a value — so callers can stop waiting.
 */
export const useLoomLandOptIn = (): {
	cohort: LoomLandOptInCohort;
	isResolved: boolean;
	isEnrolled: boolean;
	hasResolveTimedOut: boolean;
} => {
	const experiment = useExperiment(experiments.twcg2108LoomLandOptIn);
	const isEnrolled = experiment.type === EnrollmentType.ENROLLED;

	// Narrow to a single resolved-enrolled reference so reads of `value` are
	// type-safe (the not-enrolled / indeterminate arms of the union lack it).
	const resolvedExperiment =
		isEnrolled && experiment.usingDefault === false ? experiment : undefined;

	const isResolved = resolvedExperiment !== undefined;
	const cohort: LoomLandOptInCohort = resolvedExperiment?.value ?? 'control';

	// Client-only timer (effects don't run during SSR), cleared if the arm resolves.
	const isWaitingForCohort = isEnrolled && !isResolved;
	const [hasResolveTimedOut, setHasResolveTimedOut] = useState(false);

	useEffect(() => {
		if (!isWaitingForCohort) {
			return;
		}
		// Statsig already initialised and still has no value for us: `useExperiment`
		// won't re-read it, so waiting can't help — give up now rather than after
		// the timeout. The timer only covers Statsig still (or never) initialising.
		if (FeatureGates.initializeCompleted()) {
			setHasResolveTimedOut(true);
			return;
		}
		const timer = setTimeout(() => setHasResolveTimedOut(true), COHORT_RESOLVE_TIMEOUT_MS);
		return () => clearTimeout(timer);
	}, [isWaitingForCohort]);

	return { cohort, isResolved, isEnrolled, hasResolveTimedOut };
};
